import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { dimensionsListSelectorKeyValue } from '@src/store/common/dimensionList/selectors';
import { aiAlertsDimensionMappingSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';

// Dimension label/option resolver for the alert-rules dimension picker. Sourced
// from `/ai-alerts/dimension-mapping` per product context; handles the eco and
// custom-dimension cases (custom dims are keyed twice by the server, so we keep
// the first occurrence to avoid duplicate labels).
export const useDimensionLabels = () => {
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue) as Record<string, string>;
  const allDimensionMapping = useSelector(aiAlertsDimensionMappingSelector);

  const productMapping = useMemo(
    () => allDimensionMapping?.[CURRENT_PRODUCT_MODE.toLowerCase()] || {},
    [allDimensionMapping],
  );

  const resolveDimensionLabel = useCallback(
    (rawKey: string): string => {
      // Direct lookup by AGW key (e.g. 'PLAYER_NAME')
      const mapping = productMapping[rawKey];
      if (mapping) {
        // For TAG dims the lookup key is mapping.key (e.g. 'c3.player.name'), not mapping.field ('TAG')
        const lookupKey = mapping.field === 'TAG' ? mapping.key : mapping.field;
        return dimensionListObject[lookupKey] || mapping.pulse5Label || rawKey;
      }
      // Fallback: reverse-lookup for TAG key format (e.g. 'c3.player.name' stored directly)
      const reverseEntry = Object.values(productMapping as Record<string, any>).find(
        (m) => m?.key === rawKey,
      );
      if (reverseEntry) {
        return dimensionListObject[reverseEntry.key] || reverseEntry.pulse5Label || rawKey;
      }
      // Final fallback: direct dimension-list lookup by the raw key itself
      // (e.g. 'c3.video.isLive' → 'Content Category', 'OBJECT_NAME' → 'Asset Name')
      return dimensionListObject[rawKey] || rawKey;
    },
    [productMapping, dimensionListObject],
  );

  const dimensionOptions = useMemo(() => {
    const seenLabels = new Set<string>();
    return (
      Object.entries(productMapping)
        .map(([rawKey, mapping]: [string, any]) => {
          const lookupKey = mapping.field === 'TAG' ? mapping.key : mapping.field;
          return {
            value: rawKey,
            label: dimensionListObject[lookupKey] || mapping.pulse5Label || rawKey,
          };
        })
        // Custom dimensions are keyed twice by the server's getEIDimensionMappingUsingID
        // (once under the resolvedKey / raw gateway field, once under backendName), producing
        // duplicate labels. Keep the first occurrence, which is the resolvedKey the fusion-path
        // notifier matches against in alert.Dimension.
        .filter((opt) => {
          if (seenLabels.has(opt.label)) return false;
          seenLabels.add(opt.label);
          return true;
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [productMapping, dimensionListObject]);

  return { resolveDimensionLabel, dimensionOptions, productMapping };
};
